/**
 * useMultiCanvas Hook
 * Manages state and actions for multiple diagram canvases
 */

import { useState, useCallback, useEffect } from 'react'
import type { DiagramData } from '../types/schema'
import type {
  CanvasTab,
  MultiCanvasState,
  UseMultiCanvasReturn,
} from '../types/multiCanvas'

const DEFAULT_STORAGE_KEY = 'diagram-editor-multi-canvas'

// Generate unique ID
const generateId = () => `canvas-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

// Default empty diagram
const createEmptyDiagram = (name: string): DiagramData => ({
  diagramType: 'architecture',
  title: name,
  description: '',
  nodes: [],
  edges: [],
  containers: [],
  layout: {
    algorithm: 'dagre',
    direction: 'LR',
  },
})

// Load state from localStorage
const loadFromStorage = (key: string): MultiCanvasState | null => {
  try {
    const saved = localStorage.getItem(key)
    if (saved) {
      return JSON.parse(saved)
    }
  } catch (error) {
    console.error('Failed to load multi-canvas state:', error)
  }
  return null
}

// Save state to localStorage
const saveToStorage = (key: string, state: MultiCanvasState) => {
  try {
    localStorage.setItem(key, JSON.stringify(state))
  } catch (error) {
    console.error('Failed to save multi-canvas state:', error)
  }
}

export function useMultiCanvas(
  initialData?: DiagramData,
  enablePersistence = true,
  // Per-project persistence: each project's canvases live under their own key, so
  // opening a different project loads its own tab set. Defaults to the shared key.
  storageKey: string = DEFAULT_STORAGE_KEY
): UseMultiCanvasReturn {
  // Initialize state
  const [state, setState] = useState<MultiCanvasState>(() => {
    // Try to load from localStorage first
    if (enablePersistence) {
      const saved = loadFromStorage(storageKey)
      if (saved && saved.tabs.length > 0) {
        return saved
      }
    }

    // Create initial tab
    const initialTab: CanvasTab = {
      id: generateId(),
      name: 'Untitled 1',
      data: initialData || createEmptyDiagram('Untitled 1'),
      unsavedChanges: false,
      createdAt: Date.now(),
      lastModified: Date.now(),
      order: 0,
    }

    return {
      tabs: [initialTab],
      activeTabId: initialTab.id,
      nextUntitledNumber: 2,
    }
  })

  // Persist to localStorage whenever state changes
  useEffect(() => {
    if (enablePersistence) {
      saveToStorage(storageKey, state)
    }
  }, [state, enablePersistence, storageKey])

  // Add new tab
  const addTab = useCallback((name?: string, data?: DiagramData): string => {
    const newId = generateId()
    const tabName = name || `Untitled ${state.nextUntitledNumber}`
    const newOrder = state.tabs.length

    const newTab: CanvasTab = {
      id: newId,
      name: tabName,
      data: data || createEmptyDiagram(tabName),
      unsavedChanges: false,
      createdAt: Date.now(),
      lastModified: Date.now(),
      order: newOrder,
    }

    setState(prev => ({
      ...prev,
      tabs: [...prev.tabs, newTab],
      activeTabId: newId, // Switch to new tab
      nextUntitledNumber: prev.nextUntitledNumber + 1,
    }))

    return newId
  }, [state.nextUntitledNumber, state.tabs.length])

  // Remove tab
  const removeTab = useCallback((id: string) => {
    setState(prev => {
      const tabIndex = prev.tabs.findIndex(t => t.id === id)
      if (tabIndex === -1) return prev

      // Don't allow removing the last tab
      if (prev.tabs.length === 1) {
        console.warn('Cannot remove the last tab')
        return prev
      }

      const newTabs = prev.tabs.filter(t => t.id !== id)

      // If we're closing the active tab, switch to another
      let newActiveId = prev.activeTabId
      if (id === prev.activeTabId) {
        // Switch to the tab to the left, or right if it was the first tab
        const newIndex = tabIndex > 0 ? tabIndex - 1 : 0
        newActiveId = newTabs[newIndex]?.id || newTabs[0].id
      }

      // Update order for remaining tabs
      const updatedTabs = newTabs.map((tab, index) => ({
        ...tab,
        order: index,
      }))

      return {
        ...prev,
        tabs: updatedTabs,
        activeTabId: newActiveId,
      }
    })
  }, [])

  // Duplicate tab
  const duplicateTab = useCallback((id: string) => {
    setState(prev => {
      const tab = prev.tabs.find(t => t.id === id)
      if (!tab) return prev

      const newId = generateId()
      const newName = `${tab.name} (Copy)`
      const newOrder = prev.tabs.length

      const newTab: CanvasTab = {
        id: newId,
        name: newName,
        data: JSON.parse(JSON.stringify(tab.data)), // Deep clone
        unsavedChanges: true, // Mark as unsaved
        createdAt: Date.now(),
        lastModified: Date.now(),
        order: newOrder,
      }

      return {
        ...prev,
        tabs: [...prev.tabs, newTab],
        activeTabId: newId, // Switch to duplicated tab
      }
    })
  }, [])

  // Switch to tab
  const switchTab = useCallback((id: string) => {
    setState(prev => {
      if (prev.tabs.find(t => t.id === id)) {
        return { ...prev, activeTabId: id }
      }
      return prev
    })
  }, [])

  // Switch to next tab
  const switchToNext = useCallback(() => {
    setState(prev => {
      const currentIndex = prev.tabs.findIndex(t => t.id === prev.activeTabId)
      const nextIndex = (currentIndex + 1) % prev.tabs.length
      return { ...prev, activeTabId: prev.tabs[nextIndex].id }
    })
  }, [])

  // Switch to previous tab
  const switchToPrevious = useCallback(() => {
    setState(prev => {
      const currentIndex = prev.tabs.findIndex(t => t.id === prev.activeTabId)
      const prevIndex = currentIndex === 0 ? prev.tabs.length - 1 : currentIndex - 1
      return { ...prev, activeTabId: prev.tabs[prevIndex].id }
    })
  }, [])

  // Rename tab
  const renameTab = useCallback((id: string, name: string) => {
    setState(prev => ({
      ...prev,
      tabs: prev.tabs.map(tab =>
        tab.id === id
          ? { ...tab, name, lastModified: Date.now() }
          : tab
      ),
    }))
  }, [])

  // Update tab data
  const updateTabData = useCallback((id: string, data: DiagramData) => {
    setState(prev => ({
      ...prev,
      tabs: prev.tabs.map(tab =>
        tab.id === id
          ? {
              ...tab,
              data,
              lastModified: Date.now(),
              unsavedChanges: true,
            }
          : tab
      ),
    }))
  }, [])

  // Mark tab as modified
  const markTabAsModified = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      tabs: prev.tabs.map(tab =>
        tab.id === id
          ? { ...tab, unsavedChanges: true, lastModified: Date.now() }
          : tab
      ),
    }))
  }, [])

  // Mark tab as saved
  const markTabAsSaved = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      tabs: prev.tabs.map(tab =>
        tab.id === id
          ? { ...tab, unsavedChanges: false }
          : tab
      ),
    }))
  }, [])

  // Update per-canvas viewport (camera). Not an unsaved edit; don't bump lastModified.
  const updateTabViewport = useCallback(
    (id: string, viewport: { x: number; y: number; zoom: number }) => {
      setState(prev => ({
        ...prev,
        tabs: prev.tabs.map(tab =>
          tab.id === id ? { ...tab, viewport } : tab
        ),
      }))
    },
    []
  )

  // Update per-canvas selection. Not an unsaved edit; don't bump lastModified.
  const updateTabSelection = useCallback((id: string, ids: string[]) => {
    setState(prev => ({
      ...prev,
      tabs: prev.tabs.map(tab =>
        tab.id === id ? { ...tab, selection: ids } : tab
      ),
    }))
  }, [])

  // Reorder tabs
  const reorderTabs = useCallback((startIndex: number, endIndex: number) => {
    setState(prev => {
      const newTabs = [...prev.tabs]
      const [removed] = newTabs.splice(startIndex, 1)
      newTabs.splice(endIndex, 0, removed)

      // Update order property
      const updatedTabs = newTabs.map((tab, index) => ({
        ...tab,
        order: index,
      }))

      return {
        ...prev,
        tabs: updatedTabs,
      }
    })
  }, [])

  // Get active tab
  const getActiveTab = useCallback((): CanvasTab | null => {
    return state.tabs.find(t => t.id === state.activeTabId) || null
  }, [state.tabs, state.activeTabId])

  // Get tab by ID
  const getTabById = useCallback((id: string): CanvasTab | null => {
    return state.tabs.find(t => t.id === id) || null
  }, [state.tabs])

  // Check if any tab has unsaved changes
  const hasUnsavedChanges = useCallback((): boolean => {
    return state.tabs.some(tab => tab.unsavedChanges)
  }, [state.tabs])

  // Close all tabs except the specified one
  const closeOthers = useCallback((id: string) => {
    setState(prev => {
      const tabToKeep = prev.tabs.find(t => t.id === id)
      if (!tabToKeep) return prev

      return {
        ...prev,
        tabs: [tabToKeep],
        activeTabId: id
      }
    })
  }, [])

  // Close all tabs to the right of the specified tab
  const closeToRight = useCallback((id: string) => {
    setState(prev => {
      const tabIndex = prev.tabs.findIndex(t => t.id === id)
      if (tabIndex === -1 || tabIndex === prev.tabs.length - 1) return prev

      const tabsToKeep = prev.tabs.slice(0, tabIndex + 1)
      const closedActiveTab = !tabsToKeep.find(t => t.id === prev.activeTabId)

      return {
        ...prev,
        tabs: tabsToKeep,
        activeTabId: closedActiveTab ? id : prev.activeTabId
      }
    })
  }, [])

  // RT-8: create-or-update a tab keyed by a room pageId (multi-page collab).
  const upsertRoomPage = useCallback(
    (id: string, name: string, data: DiagramData, order: number) => {
      setState(prev => {
        const existing = prev.tabs.find(t => t.id === id)
        if (existing) {
          return {
            ...prev,
            tabs: prev.tabs.map(t =>
              t.id === id ? { ...t, name, data, order, lastModified: Date.now() } : t
            ),
          }
        }
        const now = Date.now()
        const newTab: CanvasTab = {
          id,
          name,
          data,
          unsavedChanges: false,
          createdAt: now,
          lastModified: now,
          order,
        }
        const tabs = [...prev.tabs, newTab].sort((a, b) => a.order - b.order)
        return { ...prev, tabs }
      })
    },
    []
  )

  // RT-8: remove tabs whose room pages vanished. Never drop below one tab; if the
  // active tab is removed, fall back to the first remaining tab.
  const removeRoomPages = useCallback((ids: string[]) => {
    const drop = new Set(ids)
    setState(prev => {
      const remaining = prev.tabs.filter(t => !drop.has(t.id))
      if (remaining.length === 0) return prev
      const activeTabId = drop.has(prev.activeTabId) ? remaining[0].id : prev.activeTabId
      return { ...prev, tabs: remaining, activeTabId }
    })
  }, [])

  return {
    // State
    tabs: state.tabs,
    activeTabId: state.activeTabId,
    nextUntitledNumber: state.nextUntitledNumber,

    // Actions
    addTab,
    upsertRoomPage,
    removeRoomPages,
    removeTab,
    duplicateTab,
    switchTab,
    switchToNext,
    switchToPrevious,
    renameTab,
    updateTabData,
    markTabAsModified,
    markTabAsSaved,
    updateTabViewport,
    updateTabSelection,
    reorderTabs,
    closeOthers,
    closeToRight,
    getActiveTab,
    getTabById,
    hasUnsavedChanges,
  }
}
